import { ShareablePreview } from "@/core/components/shared/preview-action";
import { createPreviewActions } from "@/core/components/shared/preview-action/create-preview-actions";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { apiErrorHandler } from "@/core/api/api-error-handler";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useSimulatePayrollBatch } from "@/features/payroll/api/use-simulate-payroll-batch";

/**
 * Confirmation de simulation, partagée par la fiche détail et le menu de
 * ligne. Le clic n'appelle pas l'API : la mutation part au confirm.
 *
 * Monté seulement quand `open` est vrai. `ShareablePreview` mémorise
 * `defaultOpen` au premier rendu ; le laisser monté fermé empêcherait
 * la dialog de s'ouvrir ensuite.
 */
export function PayrollSimulatePreview({
  batchId,
  label,
  lineCount,
  open,
  onOpenChange,
}: {
  batchId: string;
  label: string;
  lineCount: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTypedTranslation();
  const tPayroll = (key: string) => t(key as TFunctionType);
  const simulate = useSimulatePayrollBatch(batchId);

  if (!open) return null;

  return (
    <ShareablePreview
      defaultOpen
      onOpenChange={(next) => {
        if (!next) onOpenChange(false);
      }}
      data={{ label, lineCount }}
      title={t("payroll.actions.simulate.previewTitle")}
      description={t("payroll.actions.simulate.previewDescription")}
      actions={createPreviewActions({
        onConfirm: () =>
          simulate.mutate(undefined, {
            onSuccess: () => {
              notifSilence(t("payroll.actions.simulate.feedback"));
              onOpenChange(false);
            },
            onError: (error) =>
              notifSilenceError(apiErrorHandler(error) || error.message),
          }),
        onCancel: () => onOpenChange(false),
        t: tPayroll,
        confirmLoading: simulate.isPending,
      })}
    />
  );
}
